/** Small WebGL2 helpers: program compilation with uniform caching, textures, framebuffers. */

export class Program {
  readonly prog: WebGLProgram;
  private readonly uniforms = new Map<string, WebGLUniformLocation | null>();
  constructor(readonly gl: WebGL2RenderingContext, vsSrc: string, fsSrc: string, readonly name = 'program') {
    const vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc, name + '.vs');
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc, name + '.fs');
    const p = gl.createProgram();
    if (!p) throw new Error('createProgram failed');
    gl.attachShader(p, vs); gl.attachShader(p, fs);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      throw new Error(`link ${name}: ${gl.getProgramInfoLog(p)}`);
    }
    gl.deleteShader(vs); gl.deleteShader(fs);
    this.prog = p;
  }
  use(): this { this.gl.useProgram(this.prog); return this; }
  loc(n: string): WebGLUniformLocation | null {
    let l = this.uniforms.get(n);
    if (l === undefined) { l = this.gl.getUniformLocation(this.prog, n); this.uniforms.set(n, l); }
    return l;
  }
  f(n: string, v: number): void { this.gl.uniform1f(this.loc(n), v); }
  i(n: string, v: number): void { this.gl.uniform1i(this.loc(n), v); }
  v2(n: string, x: number, y: number): void { this.gl.uniform2f(this.loc(n), x, y); }
  v3(n: string, x: number, y: number, z: number): void { this.gl.uniform3f(this.loc(n), x, y, z); }
  v3a(n: string, a: ArrayLike<number>): void { this.gl.uniform3f(this.loc(n), a[0], a[1], a[2]); }
  v4(n: string, x: number, y: number, z: number, w: number): void { this.gl.uniform4f(this.loc(n), x, y, z, w); }
  m4(n: string, m: Float32Array): void { this.gl.uniformMatrix4fv(this.loc(n), false, m); }
}

export function compileShader(gl: WebGL2RenderingContext, type: number, src: string, name: string): WebGLShader {
  const s = gl.createShader(type);
  if (!s) throw new Error('createShader failed');
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(s) ?? '';
    const lines = src.split('\n').map((l, i) => `${i + 1}: ${l}`).join('\n');
    throw new Error(`compile ${name}:\n${log}\n${lines}`);
  }
  return s;
}

export interface RenderTarget {
  fbo: WebGLFramebuffer;
  color: WebGLTexture;
  normal: WebGLTexture | null;
  depth: WebGLTexture;
  width: number;
  height: number;
}

export function createTexture2D(gl: WebGL2RenderingContext, w: number, h: number, internal: number, format: number, type: number, filter: number = gl.NEAREST): WebGLTexture {
  const t = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.texImage2D(gl.TEXTURE_2D, 0, internal, w, h, 0, format, type, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return t;
}

/** Scene target: color + world-normal MRT with a depth texture. */
export function createRenderTarget(gl: WebGL2RenderingContext, w: number, h: number, withNormal = true): RenderTarget {
  const fbo = gl.createFramebuffer()!;
  const color = createTexture2D(gl, w, h, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.LINEAR);
  const normal = withNormal ? createTexture2D(gl, w, h, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE) : null;
  const depth = createTexture2D(gl, w, h, gl.DEPTH_COMPONENT24, gl.DEPTH_COMPONENT, gl.UNSIGNED_INT);
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, color, 0);
  if (normal) gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, normal, 0);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, depth, 0);
  gl.drawBuffers(withNormal ? [gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1] : [gl.COLOR_ATTACHMENT0]);
  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  if (status !== gl.FRAMEBUFFER_COMPLETE) throw new Error('render target incomplete: ' + status);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return { fbo, color, normal, depth, width: w, height: h };
}

export function destroyRenderTarget(gl: WebGL2RenderingContext, rt: RenderTarget): void {
  gl.deleteFramebuffer(rt.fbo); gl.deleteTexture(rt.color); gl.deleteTexture(rt.depth);
  if (rt.normal) gl.deleteTexture(rt.normal);
}

/** Depth-only shadow map with hardware comparison enabled. */
export function createShadowMap(gl: WebGL2RenderingContext, size: number): { fbo: WebGLFramebuffer; tex: WebGLTexture; size: number } {
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.DEPTH_COMPONENT24, size, size, 0, gl.DEPTH_COMPONENT, gl.UNSIGNED_INT, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_MODE, gl.COMPARE_REF_TO_TEXTURE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_FUNC, gl.LEQUAL);
  const fbo = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, tex, 0);
  gl.drawBuffers([gl.NONE]);
  gl.readBuffer(gl.NONE);
  if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) throw new Error('shadow map incomplete');
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return { fbo, tex, size };
}
